import * as React from "react";
import SignatureView from "./private/signature-view";
import type { Path } from "./private/signature-types";
import { dataURIToFile } from "./signature-utils";
import type { RenderEnvironment } from "common/components/types";
import { SignatureStyle } from "./signature-style";
import { getLocalizedString } from "common/components/localization";
import AutoSizer from "../autosizer/autosizer";
import type { ConditionVariable } from "common/condition-variable";
import { dontAwait } from "common/Support";

interface SignatureProps {
    readonly renderEnvironment: RenderEnvironment;
    readonly message: string;
    readonly shouldWriteFlag: boolean | undefined;
    readonly shouldWriteCV: ConditionVariable | undefined;
    readonly onSetFile?: (file: File) => void;
    readonly onHasPathsChanged?: (hasPaths: boolean) => void;
    readonly smoothing: number;
}

interface SignatureState {
    paths: Path[];
    lastWidth?: number;
    lastHeight?: number;
    shouldDraw: boolean;
    shouldWrite: boolean;
    cvWaitingOn: ConditionVariable | undefined;
}
class Signature extends React.PureComponent<SignatureProps, SignatureState> {
    public state: SignatureState = {
        paths: [],
        shouldDraw: false,
        shouldWrite: false,
        cvWaitingOn: undefined,
    };

    private cleanPath = (path: Path): Path => {
        if (path.length === 0) return path;
        if (path.length === 1) return [path[0], path[0]];

        const result = [path[0]];

        path.slice(1).forEach((point, index, arr) => {
            const cur = result[result.length - 1];
            const distance = Math.sqrt(Math.pow(cur.x - point.x, 2) + Math.pow(cur.y - point.y, 2));

            if (distance > this.props.smoothing || index === arr.length - 1) {
                result.push(point);
            }
        });

        return result;
    };

    private onPadDown = () => {
        const paths: Path[] = [...this.state.paths, []];
        this.setState({ paths, shouldDraw: true });
        if (paths.length === 1) {
            this.props.onHasPathsChanged?.call(undefined, true);
        }
    };

    private onPadUp = () => {
        const paths = this.state.paths;

        if (paths.length === 0 || this.state.shouldDraw === false) return;

        const newPath = this.cleanPath(paths[paths.length - 1]);
        const newPaths = [...paths];
        newPaths[paths.length - 1] = newPath;
        this.setState({ shouldDraw: false, paths: newPaths });
    };

    private onPadMove = (x: number, y: number) => {
        const { shouldDraw, paths } = this.state;
        if (shouldDraw) {
            const currentPath = paths[paths.length - 1];
            const updatedPath = [...currentPath, { x, y }];
            const newPaths = [...paths];
            newPaths[paths.length - 1] = updatedPath;
            this.setState({ paths: newPaths });
        }
    };

    private onImageData = (dataURI: string) => {
        const { onSetFile } = this.props;
        const file = dataURIToFile(dataURI);
        if (onSetFile === undefined || file === undefined) return;
        onSetFile(file);
    };

    private onClear = () => {
        this.setState({ paths: [] });
        this.props.onHasPathsChanged?.call(undefined, false);
    };

    public componentDidUpdate(): void {
        if (this.state.shouldWrite) return;

        if (this.props.shouldWriteFlag === true) {
            this.setState({ shouldWrite: true });
        }

        const { shouldWriteCV } = this.props;
        if (shouldWriteCV !== undefined && shouldWriteCV !== this.state.cvWaitingOn) {
            dontAwait(shouldWriteCV.wait().then(() => this.setState({ shouldWrite: true })));
            this.setState({ cvWaitingOn: shouldWriteCV });
        }
    }

    render() {
        const { renderEnvironment, message } = this.props;
        const { paths, lastWidth, lastHeight, shouldWrite } = this.state;

        return (
            <SignatureStyle data-test="app-signature-view">
                <AutoSizer>
                    {p => {
                        if (p.width !== lastWidth || p.height !== lastHeight) {
                            setTimeout(() => {
                                this.setState({
                                    paths: [],
                                    lastWidth: p.width,
                                    lastHeight: p.height,
                                });
                            }, 0);
                        }
                        return (
                            <SignatureView
                                renderEnvironment={renderEnvironment}
                                shouldWrite={shouldWrite}
                                message={message}
                                width={p.width ?? 100}
                                height={p.height ?? 100}
                                paths={paths}
                                onPadDown={this.onPadDown}
                                onPadUp={this.onPadUp}
                                onPadMove={this.onPadMove}
                                onImageData={this.onImageData}
                            />
                        );
                    }}
                </AutoSizer>
                <button
                    className={paths.length > 0 ? "s-clear-button" : "s-clear-button disabled"}
                    onClick={paths.length > 0 ? this.onClear : undefined}>
                    {getLocalizedString("clear", renderEnvironment.appEnvironment.appKind)}
                </button>
            </SignatureStyle>
        );
    }
}

export default Signature;
